/* Native document scrolling; thumbnails point to the existing product media. */
class KStackedGalleryNav extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.gallery = this.closest('media-gallery');
    this.scroller = this.closest('.page-wrapper') || document.scrollingElement;
    this.links = [...this.querySelectorAll('a[data-k-media-index]')];
    this.items = this.links.map(link => this.gallery.querySelector(`[id="${link.getAttribute('href').slice(1)}"]`));
    this.mobileItems = [...this.gallery.querySelectorAll('slideshow-slide')];
    this.list = this.querySelector('.k-gallery-thumbnails');
    this.prevButton = this.querySelector('[data-k-gallery-prev]');
    this.nextButton = this.querySelector('[data-k-gallery-next]');
    this.activeIndex = -1;
    // Mobile uses the theme slideshow; desktop -2 uses the stacked grid.
    this.mediaScroller = this.gallery.querySelector('slideshow-slides') || this.gallery.querySelector('.media-gallery__grid');
    const shiftThumbnails = direction => {
      if (!this.list) return;
      const first = this.links[0];
      const step = (first?.getBoundingClientRect().width || 42) + 4;
      const max = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      const left = Math.max(0, Math.min(max, this.list.scrollLeft + direction * step));
      this.list.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    this.prevButton?.addEventListener('click', () => shiftThumbnails(-1), { signal });
    this.nextButton?.addEventListener('click', () => shiftThumbnails(1), { signal });
    this.addEventListener('click', event => {
      const link = event.target.closest('a[data-k-media-index]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const index = Number(link.dataset.kMediaIndex);
      const item = innerWidth < 750 ? this.mobileItems[index] : this.items[index];
      if (!item) return;
      event.preventDefault();
      this.select(index);
      if (innerWidth < 750) {
        item.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest', inline: 'start' });
        return;
      }
      const top = this.scroller.scrollTop + item.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top - 90;
      this.scroller.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    const queue = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    };
    this.slideObserver = new MutationObserver(queue);
    this.mobileItems.forEach(item => this.slideObserver.observe(item, { attributes: true, attributeFilter: ['aria-hidden', 'class'] }));
    this.poller = window.setInterval(queue, 200);
    window.addEventListener('scroll', queue, { passive: true, signal });
    document.addEventListener('scroll', queue, { capture: true, passive: true, signal });
    window.addEventListener('resize', queue, { passive: true, signal });
    this.mediaScroller?.addEventListener('scroll', queue, { passive: true, signal });
    this.list?.addEventListener('scroll', queue, { passive: true, signal });
    this.update();
  }
  select(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.links.length) return;
    const changed = this.activeIndex !== index;
    this.activeIndex = index;
    this.links.forEach((link, position) => link.setAttribute('aria-current', String(position === index)));
    const selected = this.links[index];
    if (changed && selected && this.list) {
      if (innerWidth < 750 && this.list.scrollWidth > this.list.clientWidth) {
        const left = selected.offsetLeft;
        const right = left + selected.offsetWidth;
        if (left < this.list.scrollLeft) this.list.scrollLeft = left;
        else if (right > this.list.scrollLeft + this.list.clientWidth) this.list.scrollLeft = right - this.list.clientWidth;
      } else if (this.list.scrollHeight > this.list.clientHeight) {
        const top = selected.offsetTop - this.list.offsetTop;
        if (top < this.list.scrollTop) this.list.scrollTop = top;
        else if (top + selected.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = top + selected.offsetHeight - this.list.clientHeight;
      }
    }
    this.updateArrows();
  }
  updateArrows() {
    if (!this.list || !this.prevButton || !this.nextButton) return;
    const mobile = innerWidth < 750;
    this.prevButton.disabled = !mobile || this.list.scrollLeft <= 1;
    this.nextButton.disabled = !mobile || this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 1;
  }
  update() {
    if (innerWidth < 750) {
      const width = this.mobileItems[0]?.getBoundingClientRect().width || this.mediaScroller?.clientWidth || 1;
      const position = this.mediaScroller ? this.mediaScroller.scrollLeft / width : 0;
      const active = Math.max(0, Math.min(this.mobileItems.length - 1, Math.round(position)));
      this.select(active);
      return;
    }
    const line = Math.max(90, innerHeight * 0.35);
    let active = 0;
    this.items.forEach((item, index) => { if (item && item.getBoundingClientRect().top <= line) active = index; });
    this.select(active);
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.slideObserver?.disconnect();
    window.clearInterval(this.poller);
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('k-stacked-gallery-nav')) customElements.define('k-stacked-gallery-nav', KStackedGalleryNav);
